<template>
  <mf-card-container title="Quantidade por loja" description="Análise por loja das unidades vendidas na oferta.">
    <template v-slot:filters>
      <div class="slot-filters">
        <export-data v-if="isZeDelivery" :data-fetcher="getDataToExport" filename="quantidade_por_loja" ghost />
        <div class="width-345">
          <v-autocomplete v-model="selectedUnits" :items="units" label="Lojas" multiple hide-details outlined>
            <template v-slot:no-data><span class="px-4">Nenhuma loja encontrada</span></template>

            <template v-if="units.length" v-slot:prepend-item>
              <v-list-item ripple @mousedown.prevent @click="toggleAllUnits">
                <v-list-item-action>
                  <v-icon :color="selectedUnits.length > 0 ? 'primary' : ''">
                    {{ icon }}
                  </v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>Todas as lojas</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-1" />
            </template>

            <template v-slot:selection="{ item, index }">
              <v-chip v-if="index === 0 && selectedUnits.length === 1">{{ item }}</v-chip>
              <v-chip v-if="index === 0 && selectedUnits.length > 1 && selectedUnits.length < units.length">{{ selectedUnits.length }} selecionadas</v-chip>
              <v-chip v-if="index === 0 && selectedUnits.length === units.length">Todas</v-chip>
            </template>
          </v-autocomplete>
        </div>
      </div>
    </template>

    <mf-tabs-menu v-model="tab" :tabs="tabItems" class="my-4" />
    <sold-products-chart v-if="tab === 0" :selected-units="selectedUnits" :items="items" :offer-type="offerType" />
    <clients-discounts-chart
      v-if="tab === 1 && !isNextbuyCashback && !isDigitalVoucher"
      :selected-units="selectedUnits"
      :items="items"
      :offer-type="offerType"
    />
    <benefit-clients-chart
      v-else-if="tab === 1 && (isNextbuyCashback || isDigitalVoucher)"
      :selected-units="selectedUnits"
      :items="items"
      :offer-type="offerType"
    />
    <benefit-values-chart
      v-if="tab === 2 && (isNextbuyCashback || isDigitalVoucher || isZeDelivery)"
      :selected-units="selectedUnits"
      :items="items"
      :offer-type="offerType"
    />
  </mf-card-container>
</template>

<script>
import { localeString } from '@/config/helpers.js'
import SoldProductsChart from './SoldProductsChart.vue'
import ClientsDiscountsChart from './ClientsDiscountsChart.vue'
import BenefitClientsChart from './BenefitClientsChart.vue'
import BenefitValuesChart from './BenefitValuesChart.vue'
import ExportData from '@/components/export-data/ExportData.vue'

export default {
  name: 'SalesPerUnit',
  components: {
    SoldProductsChart,
    ClientsDiscountsChart,
    BenefitClientsChart,
    BenefitValuesChart,
    ExportData,
  },
  props: {
    metrics: {
      type: Array,
      default(_) {
        return []
      },
    },
    offerType: {
      type: String,
      validator: (value) => ['takepay', 'nextbuycashback', 'dynamic_rule', 'general', 'crosssell', 'percentage', 'benefits'].includes(value),
    },
  },
  data: () => ({
    locale: localeString,
    tab: 0,
    tabItems: [
      {
        name: 'Produtos vendidos',
        itemsLength: 0,
        haveLength: false,
      },
      {
        name: 'Clientes e descontos',
        itemsLength: 0,
        haveLength: false,
      },
    ],
    selectedUnits: [],
  }),
  computed: {
    items() {
      const items = []
      if (!this.metrics) return items

      for (const unit of this.metrics) {
        const unitMetricsOfferType = unit.metrics?.[this.offerType]

        if (this.isZeDelivery) {
          items.push({
            unit_name: unit.unit_name,
            sold_products: unitMetricsOfferType?.products_metrics?.totals?.total?.quantity || 0,
            eligible_products: 0,
            free_products: unitMetricsOfferType?.products_metrics?.totals?.sales?.quantity || 0,
            clients_with_discount: unitMetricsOfferType?.benefits_metrics_totals_consumed?.clients || 0,
            offer_discount: unitMetricsOfferType?.benefits_metrics_totals_consumed?.discounts || 0,
            earn_quantity: 0,
            earn_value: 0,
            burn_quantity: unitMetricsOfferType?.benefits_metrics_totals_consumed?.quantity || 0,
            burn_value: unitMetricsOfferType?.benefits_metrics_totals_consumed?.discounts || 0,
            earn_clients: 0,
            burn_clients: 0,
          })
        } else {
          items.push({
            unit_name: unit.unit_name,
            sold_products: unitMetricsOfferType?.products_metrics?.totals?.total?.quantity || 0,
            eligible_products: unitMetricsOfferType?.eligible_metrics?.quantity || 0,
            free_products: this.isCrossSell
              ? unitMetricsOfferType?.products_target_metrics?.totals?.sales?.quantity || 0
              : unitMetricsOfferType?.products_metrics?.totals?.sales?.quantity || 0,
            clients_with_discount: unitMetricsOfferType?.products_metrics?.totals?.sales?.clients || 0,
            offer_discount: this.calcDiscount(unitMetricsOfferType, this.offerType),
            earn_quantity: unitMetricsOfferType?.earn_metrics?.count || 0,
            earn_value: unitMetricsOfferType?.earn_metrics?.total_value || 0,
            burn_quantity: unitMetricsOfferType?.burn_metrics?.count || 0,
            burn_value: unitMetricsOfferType?.burn_metrics?.total_value || 0,
            earn_clients: unitMetricsOfferType?.earn_metrics?.clients || 0,
            burn_clients: unitMetricsOfferType?.burn_metrics?.clients || 0,
          })
        }
      }
      return items
    },

    units() {
      const units = []
      if (!this.metrics) return units

      for (const unit of this.metrics) {
        units.push(unit.unit_name)
      }
      return units
    },

    isNextbuyCashback() {
      return this.offerType === 'nextbuycashback'
    },

    isDigitalVoucher() {
      return this.offerType === 'dynamic_rule'
    },

    isCrossSell() {
      return this.offerType === 'crosssell'
    },

    isZeDelivery() {
      return this.offerType === 'benefits'
    },

    offerTypeName() {
      return this.isNextbuyCashback ? 'cashback' : this.isDigitalVoucher || this.isZeDelivery ? 'voucher' : ''
    },

    offerTypeNameCapital() {
      return this.isNextbuyCashback ? 'Cashback' : this.isDigitalVoucher || this.isZeDelivery ? 'Voucher' : ''
    },

    icon() {
      if (this.selectedUnits.length === this.units.length) return 'mdi-checkbox-marked'
      if (this.selectedUnits.length > 0) return 'mdi-minus-box'
      return 'mdi-checkbox-blank-outline'
    },
  },
  mounted() {
    if (this.isNextbuyCashback || this.isDigitalVoucher || this.isZeDelivery) {
      this.tabItems.push({
        name: `${this.offerTypeNameCapital} por loja`,
        itemsLength: 0,
        haveLength: false,
      })
    }
    this.selectedUnits = this.units
  },
  methods: {
    toggleAllUnits() {
      this.selectedUnits = this.selectedUnits.length === this.units.length ? [] : this.units
    },
    calcDiscount(unitMetricsOfferType, offerType) {
      if (!unitMetricsOfferType) return 0
      let discount = 0
      let productsMetricsTable = unitMetricsOfferType?.products_metrics?.table
      if (offerType === 'crosssell') {
        productsMetricsTable = unitMetricsOfferType?.products_target_metrics?.table
      }
      productsMetricsTable?.forEach((product) => {
        discount += product?.sales?.rebate || 0
      })
      return discount
    },
    getDataToExport() {
      const headers = ['Loja']
      let rows = []
      if (this.isZeDelivery) {
        headers.push('Unidades vendidas total', 'Unidades vendidas com voucher', 'Clientes com desconto', 'Valor de desconto', 'Qtd. de vouchers consumidos')
        rows = this.items.map((item) => [
          item.unit_name,
          item.sold_products,
          item.free_products,
          item.clients_with_discount,
          item.offer_discount,
          item.burn_quantity,
        ])
      }
      return { headers, rows }
    },
  },
}
</script>

<style lang="scss" scoped>
.slot-filters {
  display: flex;
  align-items: center;
}

.width-345 {
  width: 345px;
}
</style>
